import React, { useState, useMemo } from 'react';
import {
  FinancialReportsGrid,
  FinancialsErrorBoundary,
  NoFinanciaData,
  FinancialPaymentHistoryTable,
  StripeConnectionSetup,
} from '../components';
import { Dropdown, LoadingState, PermissionGuard } from '@/shared';
import type { ResidentPaymentHistory } from '../components/FinancialPaymentHistoryTable';
import { Link } from '@tanstack/react-router';
import { useFinancialOverview, useResidentFinancialHistory } from '../hooks';
import { useAuth } from '@/app/providers';
import { useCurrentUserPermissions } from '@/shared/hooks/useCurrentUserPermissions';

type FinancialTimeRange = 30 | 60 | 90;

// ---------- Component ----------
const FinancialsPageContent: React.FC = () => {
  const { user } = useAuth();
  const [selectedRange, setSelectedRange] = useState<FinancialTimeRange>(30);
  const { data: overviewData, isLoading: isOverviewLoading } =
    useFinancialOverview(selectedRange);

  const { hasPermission } = useCurrentUserPermissions();
  const canViewHistory = hasPermission('managePayments');

  const [page] = useState(1);
  const { data: historyData, isLoading: isHistoryLoading } =
    useResidentFinancialHistory(
      {
        page,
        limit: 10,
      },
      { enabled: canViewHistory },
    );

  const processedData = useMemo(() => {
    if (!overviewData?.data) return null;
    console.log('overviewData', overviewData);
    const { metrics } = overviewData.data;

    return {
      earning: metrics.earnings.value,
      change: parseFloat(metrics.earnings.change),
      cards: [
        {
          title: metrics.activeResidents.label,
          value: metrics.activeResidents.value as number,
          change: parseFloat(metrics.activeResidents.change),
          formattedValue: metrics.activeResidents.value.toString(),
        },
        {
          title: metrics.outstandingBalance.label,
          value: 0, // Value is string "$0", need to handle if number required
          change: parseFloat(metrics.outstandingBalance.change),
          formattedValue: metrics.outstandingBalance.value.toString(),
        },
        {
          title: metrics.outstandingFeesCollected.label,
          value: 0,
          change: parseFloat(metrics.outstandingFeesCollected.change),
          formattedValue: metrics.outstandingFeesCollected.value.toString(),
        },
        {
          title: metrics.customChargesAdded.label,
          value: 0,
          change: parseFloat(metrics.customChargesAdded.change),
          formattedValue: metrics.customChargesAdded.value.toString(),
        },
      ],
    };
  }, [overviewData]);

  const residentPaymentHistoryData: ResidentPaymentHistory[] = useMemo(() => {
    if (!historyData?.data?.reports) return [];
    return historyData.data.reports.map((report) => {
      let status: 'PAID' | 'PARTIAL' | 'UNPAID' = 'UNPAID';

      // Map API status to UI status
      if (report.status === 'Paid') {
        status = 'PAID';
      } else if (report.paidAmount > 0 && report.outstandingFee > 0) {
        status = 'PARTIAL';
      } else if (report.outstandingFee > 0) {
        status = 'UNPAID'; // or keep as UNPAID default
      }

      return {
        id: report.residentProfileId,
        residentName: report.residentName,
        houseNo: report.houseName,
        programFees: report.programFees,
        customCharges: report.customCharges,
        outstandingFee: report.outstandingFee,
        totalDue: report.totalDue,
        paidAmount: report.paidAmount,
        lastPayment: report.lastPayment || '',
        status,
      };
    });
  }, [historyData]);

  if (isOverviewLoading || isHistoryLoading) {
    return <LoadingState message="Loading financial data..." fullScreen />;
  }

  if (!processedData) {
    return <NoFinanciaData />;
  }

  const { earning, change, cards } = processedData;

  const timeOptions = [
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 60 days', value: 60 },
    { label: 'Last 90 days', value: 90 },
  ];

  return (
      <div className="min-h-screen bg-background w-full">
        {!overviewData ? (
          <NoFinanciaData />
        ) : (
          <div>
            {/* Header */}
            <div className="mb-8 px-0 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-2 py-2">
                <p className="text-lg md:text-xl text-text-secondary font-bold">
                  This month earning
                </p>

                {/* Dropdown */}
                <div className="relative">
                  <Dropdown
                    options={timeOptions}
                    value={selectedRange}
                    onChange={(val) =>
                      setSelectedRange(val as FinancialTimeRange)
                    }
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Earnings Summary */}
              <div className="flex items-center gap-4">
                <span className="text-3xl md:text-5xl text-text-primary font-bold">
                  {earning}
                </span>
                <span
                  className={`text-lg md:text-2xl font-medium ${
                    change >= 0 ? 'text-[#177D3D]' : 'text-red-500'
                  }`}
                >
                  {change > 0 ? `+${change}%` : `${change}%`}
                </span>
              </div>

              {/* Progress Bars */}
              <div className="grid grid-cols-12 gap-2 my-4 md:my-9">
                <div className="h-[6px] bg-[#B6B0FB] col-span-4" />
                <div className="h-[6px] bg-[#BBEEED] col-span-1" />
                <div className="h-[6px] bg-[#FBDE9D] col-span-3" />
                <div className="h-[6px] bg-[#95A1FC] col-span-4" />
              </div>

              {/* Overview Cards */}
              <div className="grid grid-cols-2 gap-4">
                {cards.map((card, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-white rounded-xl shadow-[0_2px_4px_-1px_#0000000F,0_4px_6px_-1px_#0000001A] border border-gray-100"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-[#B6B0FB] h-2 w-2 rounded-full" />
                      <span className="text-sm md:text-base text-text-secondary">{card.title}</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-lg md:text-xl text-text-primary font-bold">
                        {card.formattedValue}
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          card.change >= 0 ? 'text-[#177D3D]' : 'text-red-500'
                        }`}
                      >
                        {card.change > 0 ? `+${card.change}%` : `${card.change}%`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {user?.role === 'OWNER' && (
              <div className="mb-8 px-0 sm:px-6 lg:px-8">
                <StripeConnectionSetup />
              </div>
            )}

            <div className="mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
              <PermissionGuard
                permission="managePayments"
                fallback={<div />}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-text-primary text-lg md:text-xl font-bold">
                    Resident payment history
                  </h3>
                  <Link
                    to="/financials/history"
                    className="text-[#357AE1] cursor-pointer hover:underline"
                  >
                    See more
                  </Link>
                </div>
              </PermissionGuard>
            </div>

            <div className="mb-8 overflow-x-auto">
              <PermissionGuard
                permission="managePayments"
                fallback={<div />}
              >
                <FinancialPaymentHistoryTable
                  data={residentPaymentHistoryData}
                />
              </PermissionGuard>
            </div>

            <div className="mb-8 px-0 sm:px-6 lg:px-8">
              <PermissionGuard permission="viewReports" fallback={<div />}>
                <FinancialReportsGrid />
              </PermissionGuard>
            </div>
          </div>
        )}
      </div>
  );
};

// ---------- Wrapper with Error Boundary ----------
export const FinancialsPage: React.FC = () => (
  <FinancialsErrorBoundary onRetry={() => window.location.reload()}>
    <FinancialsPageContent />
  </FinancialsErrorBoundary>
);
